import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { Logo } from "../components/brand/Logo";
import { api, apiError } from "../lib/api";

const STATUSES = ["new", "contacted", "qualified", "closed"];

const Login = ({ onDone }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", f); onDone(data); } catch (x) { setErr(apiError(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-24 w-full max-w-sm space-y-6 bg-[#F8F3E9] p-8" data-testid="admin-login-form">
      <h1 className="text-4xl">Enquiry dashboard</h1>
      <div><label htmlFor="ae" className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#8C6A3C]">Email</label>
        <input id="ae" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field-lux" data-testid="admin-email-input" /></div>
      <div><label htmlFor="ap" className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#8C6A3C]">Password</label>
        <input id="ap" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="field-lux" data-testid="admin-password-input" /></div>
      {err && <p role="alert" className="text-sm text-[#A2412F]" data-testid="admin-login-error">{err}</p>}
      <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="admin-login-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Sign in</span>}</button>
    </form>
  );
};

const Row = ({ e, onStatus }) => (
  <tr className="border-b border-[#B18A4A]/15 align-top" data-testid="admin-enquiry-row">
    <td className="py-4 pr-4 text-xs text-[#746D64]">{new Date(e.created_at).toLocaleString()}</td>
    <td className="py-4 pr-4"><p className="font-semibold">{e.full_name}</p><p className="text-xs text-[#746D64]">{e.company} · {e.city}</p></td>
    <td className="py-4 pr-4 text-sm"><a href={`mailto:${e.email}`} className="underline">{e.email}</a><p className="text-xs">{e.phone}</p></td>
    <td className="py-4 pr-4 text-sm"><p>{e.enquiry_type}</p><p className="text-xs text-[#746D64]">{e.business_type} · {e.monthly_quantity} · {e.bottle_format}</p>{e.message && <p className="mt-2 max-w-xs text-xs italic">“{e.message}”</p>}</td>
    <td className="py-4 pr-4 text-xs">{e.email_status === "sent" ? "Emailed" : e.email_status}</td>
    <td className="py-4">
      <select value={e.status} onChange={(x) => onStatus(e.id, x.target.value)} className="field-lux !py-1 text-sm" aria-label="Status" data-testid={`admin-status-${e.id}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </td>
  </tr>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/enquiries"); setItems(Array.isArray(data) ? data : []); setErr(""); } catch (x) { setErr(apiError(x)); }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const onStatus = async (id, status) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/admin/enquiries/${id}`, { status }); } catch (x) { setErr(apiError(x)); load(); }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(null); setItems([]); };

  return (
    <div className="min-h-screen bg-[#21170F] pb-20">
      <header className="mx-auto flex max-w-[1300px] items-center justify-between px-5 py-6 sm:px-8">
        <Logo tone="ivory" />
        {user && <div className="flex gap-3">
          <button type="button" onClick={load} className="btn-line !min-h-[40px] !py-2" data-testid="admin-refresh-btn"><RefreshCw className="h-3.5 w-3.5" /><span>Refresh</span></button>
          <button type="button" onClick={logout} className="btn-line !min-h-[40px] !py-2" data-testid="admin-logout-btn"><LogOut className="h-3.5 w-3.5" /><span>Sign out</span></button>
        </div>}
      </header>
      {!checked ? null : !user ? <Login onDone={setUser} /> : (
        <main className="mx-auto max-w-[1300px] px-5 sm:px-8">
          <div className="overflow-x-auto bg-[#F8F3E9] p-6 sm:p-8">
            <h1 className="text-4xl">Enquiries <span className="text-2xl text-[#B18A4A]" data-testid="admin-enquiry-count">({items.length})</span></h1>
            {err && <p role="alert" className="mt-4 text-sm text-[#A2412F]">{err}</p>}
            <table className="mt-6 w-full min-w-[900px] text-left text-[#26231F]">
              <thead><tr className="border-b border-[#B18A4A]/40 text-[10px] uppercase tracking-[0.2em] text-[#8C6A3C]">
                <th className="pb-3">Received</th><th className="pb-3">Contact</th><th className="pb-3">Reach</th><th className="pb-3">Enquiry</th><th className="pb-3">Notification</th><th className="pb-3">Status</th>
              </tr></thead>
              <tbody>{items.map((e) => <Row key={e.id} e={e} onStatus={onStatus} />)}</tbody>
            </table>
            {items.length === 0 && <p className="py-10 text-center text-[#746D64]">No enquiries yet.</p>}
          </div>
        </main>
      )}
    </div>
  );
}
